import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

import { LessonEndingSoonEvent } from "../types/events";

type ClassroomView = "prejoin" | "call" | "whiteboard" | "screenshare" | "video";

interface MeetingError {
  code: string;
  message: string;
}

interface ClassroomStore {
  meetingId: string | null;
  setMeetingId: (meetingId: string | null) => void;
  token: string | null;
  setToken: (token: string | null) => void;
  tokenExpiresAt: string | null;
  setTokenExpiresAt: (expiresAt: string | null) => void;
  view: ClassroomView;
  setView: (view: ClassroomView) => void;
  isJoined: boolean;
  setIsJoined: (isJoined: boolean) => void;
  selectedWebcam: string;
  setSelectedWebcam: (deviceId: string) => void;
  selectedMic: string;
  setSelectedMic: (deviceId: string) => void;
  selectedSpeaker: string;
  setSelectedSpeaker: (deviceId: string) => void;
  webcamOn: boolean;
  setWebcamOn: (on: boolean) => void;
  micOn: boolean;
  setMicOn: (on: boolean) => void;
  presenterId: string | null;
  setPresenterId: (participantId: string | null) => void;
  isScreenSharing: boolean;
  customTracks: {
    micTrack: MediaStream | null;
    webcamTrack: MediaStream | null;
  };
  setCustomTracks: (tracks: {
    micTrack: MediaStream | null;
    webcamTrack: MediaStream | null;
  }) => void;

  // PiP functionality
  isPipMode: boolean;
  setIsPipMode: (isPipMode: boolean) => void;
  // Removed activeSpeakerId as we'll use it directly from the SDK
  videoRoomData: any | null;
  setVideoRoomData: (data: any | null) => void;

  // Meeting lifecycle management
  shouldMaintainConnection: boolean;
  setShouldMaintainConnection: (maintain: boolean) => void;

  // Clear function that respects connection maintenance
  clearMeetingData: () => void;

  // Meeting initialization state
  isMeetingInitialized: boolean;
  setMeetingInitialized: (initialized: boolean) => void;

  // Add isInitialized state to track when meeting is ready for hooks
  isInitialized: boolean;
  setIsInitialized: (initialized: boolean) => void;

  lessonLocation: string | undefined;
  setLessonLocation: (location: string) => void;

  // Add chat state
  isChatOpen: boolean;
  setIsChatOpen: (isOpen: boolean) => void;
  toggleChat: () => void;

  // PiP transition tracking
  isTransitioningFromPiP: boolean;
  setIsTransitioningFromPiP: (isTransitioning: boolean) => void;

  // Loading state
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;

  // Fullscreen whiteboard state
  isWhiteboardFullscreen: boolean;
  setIsWhiteboardFullscreen: (isFullscreen: boolean) => void;

  // Mobile participant visibility for fullscreen activities
  showParticipantsOnMobile: boolean;
  setShowParticipantsOnMobile: (show: boolean) => void;

  // Unread message notifications
  unreadMessageCount: number;
  setUnreadMessageCount: (count: number) => void;
  incrementUnreadMessageCount: () => void;
  clearUnreadMessageCount: () => void;

  lessonId: string | undefined;
  setLessonId: (lessonId: string | undefined) => void;

  // Lesson ended state
  lessonEnded: boolean;
  setLessonEnded: (ended: boolean) => void;

  // Lesson extension state
  extensionInfo: { roomId: string; eventData: LessonEndingSoonEvent } | null;
  setExtensionInfo: (info: { roomId: string; eventData: LessonEndingSoonEvent } | null) => void;
  extensionDialogOpen: boolean;
  setExtensionDialogOpen: (open: boolean) => void;
  lessonEndTime: string | null;
  setLessonEndTime: (endTime: string | null) => void;

  // Simplified video sync state
  videoSync: {
    videoId: string;
    time: number;
    isPlaying: boolean;
    lastUpdate: number;
  } | null;
  setVideoSync: (
    sync: {
      videoId: string;
      time: number;
      isPlaying: boolean;
      lastUpdate: number;
    } | null,
  ) => void;

  // Recording state
  isRecording: boolean;
  setIsRecording: (isRecording: boolean) => void;
  recordingStartTime: number | null;
  setRecordingStartTime: (startTime: number | null) => void;
  recordingBlob: Blob | null;
  setRecordingBlob: (blob: Blob | null) => void;

  // Meeting error state
  meetingError: MeetingError | null;
  setMeetingError: (error: MeetingError | null) => void;
  clearMeetingError: () => void;
}

export const useClassroomStore = create<ClassroomStore>()(
  subscribeWithSelector((set, get) => ({
    meetingId: null,
    setMeetingId: (meetingId: string | null) => set({ meetingId }),
    token: null,
    setToken: (token: string | null) => set({ token }),
    tokenExpiresAt: null,
    setTokenExpiresAt: (expiresAt: string | null) => set({ tokenExpiresAt: expiresAt }),
    view: "prejoin",
    setView: (view: ClassroomView) => set({ view }),
    isJoined: false,
    setIsJoined: (isJoined: boolean) => set({ isJoined }),
    selectedWebcam: localStorage.getItem("selectedWebcam") || "",
    setSelectedWebcam: (deviceId: string) => {
      localStorage.setItem("selectedWebcam", deviceId);
      set({ selectedWebcam: deviceId });
    },
    selectedMic: localStorage.getItem("selectedMic") || "",
    setSelectedMic: (deviceId: string) => {
      localStorage.setItem("selectedMic", deviceId);
      set({ selectedMic: deviceId });
    },
    selectedSpeaker: localStorage.getItem("selectedSpeaker") || "",
    setSelectedSpeaker: (deviceId: string) => {
      localStorage.setItem("selectedSpeaker", deviceId);
      set({ selectedSpeaker: deviceId });
    },
    // Store user's preference for webcam/mic state
    webcamOn:
      localStorage.getItem("webcamOn") === "true" || localStorage.getItem("webcamOn") === null,
    setWebcamOn: (on: boolean) => {
      localStorage.setItem("webcamOn", String(on));
      set({ webcamOn: on });
    },
    micOn: localStorage.getItem("micOn") === "true" || localStorage.getItem("micOn") === null,
    setMicOn: (on: boolean) => {
      localStorage.setItem("micOn", String(on));
      set({ micOn: on });
    },
    presenterId: null,
    setPresenterId: (participantId: string | null) => set({ presenterId: participantId }),
    get isScreenSharing() {
      return get().presenterId !== null;
    },
    customTracks: {
      micTrack: null,
      webcamTrack: null,
    },
    setCustomTracks: (tracks) => set({ customTracks: tracks }),

    // PiP functionality
    isPipMode: false,
    setIsPipMode: (isPipMode: boolean) => set({ isPipMode }),
    // Removed activeSpeakerId and setActiveSpeakerId
    videoRoomData: null,
    setVideoRoomData: (data: any | null) => set({ videoRoomData: data }),

    // Meeting lifecycle management
    shouldMaintainConnection: false,
    setShouldMaintainConnection: (maintain: boolean) => set({ shouldMaintainConnection: maintain }),

    // Clear function that respects connection maintenance
    clearMeetingData: () => {
      const { shouldMaintainConnection } = get();

      if (!shouldMaintainConnection) {
        set({
          meetingId: null,
          videoRoomData: null,
          isJoined: false,
          isPipMode: false,
          // Removed activeSpeakerId: null,
          presenterId: null,
          view: "prejoin",
          isMeetingInitialized: false,
          isInitialized: false, // Reset initialization state
          isChatOpen: false, // Reset chat state
          isTransitioningFromPiP: false, // Reset PiP transition state
          isLoading: true, // Reset loading state
          unreadMessageCount: 0, // Reset unread message count
          lessonEnded: false, // Reset lesson ended flag
          // Reset video sync state
          videoSync: null,
          // Reset recording state
          isRecording: false,
          recordingStartTime: null,
          recordingBlob: null,
          // Reset meeting error state
          meetingError: null,
        });
      }
    },

    // Meeting initialization state
    isMeetingInitialized: false,
    setMeetingInitialized: (initialized: boolean) => set({ isMeetingInitialized: initialized }),

    // Add isInitialized state to track when meeting is ready for hooks
    isInitialized: false,
    setIsInitialized: (initialized: boolean) => set({ isInitialized: initialized }),

    lessonLocation: undefined,
    setLessonLocation: (location) => set({ lessonLocation: location }),

    // Add chat state
    isChatOpen: false,
    setIsChatOpen: (isOpen: boolean) => {
      set({ isChatOpen: isOpen });
      // Clear unread messages when chat is opened
      if (isOpen) {
        set({ unreadMessageCount: 0 });
      }
    },
    toggleChat: () => {
      const { isChatOpen } = get();
      const newState = !isChatOpen;

      set({ isChatOpen: newState });
      // Clear unread messages when chat is opened
      if (newState) {
        set({ unreadMessageCount: 0 });
      }
    },

    // PiP transition tracking
    isTransitioningFromPiP: false,
    setIsTransitioningFromPiP: (isTransitioning: boolean) =>
      set({ isTransitioningFromPiP: isTransitioning }),

    // Loading state - initially true, but will be managed by components
    isLoading: true,
    setIsLoading: (isLoading: boolean) => set({ isLoading }),

    // Fullscreen whiteboard state
    isWhiteboardFullscreen: false,
    setIsWhiteboardFullscreen: (isFullscreen: boolean) =>
      set({ isWhiteboardFullscreen: isFullscreen }),

    // Mobile participant visibility for fullscreen activities
    showParticipantsOnMobile: true,
    setShowParticipantsOnMobile: (show: boolean) => set({ showParticipantsOnMobile: show }),

    // Unread message notifications
    unreadMessageCount: 0,
    setUnreadMessageCount: (count: number) => set({ unreadMessageCount: count }),
    incrementUnreadMessageCount: () =>
      set((state) => ({ unreadMessageCount: state.unreadMessageCount + 1 })),
    clearUnreadMessageCount: () => set({ unreadMessageCount: 0 }),
    lessonId: undefined,
    setLessonId: (lessonId: string | undefined) => set({ lessonId }),

    // Lesson ended state
    lessonEnded: false,
    setLessonEnded: (ended: boolean) => set({ lessonEnded: ended }),

    // Lesson extension state
    extensionInfo: JSON.parse(localStorage.getItem("extensionInfo") || "null"),
    setExtensionInfo: (info: { roomId: string; eventData: any } | null) => {
      localStorage.setItem("extensionInfo", JSON.stringify(info));
      set({ extensionInfo: info });
    },
    extensionDialogOpen: false,
    setExtensionDialogOpen: (open: boolean) => set({ extensionDialogOpen: open }),
    lessonEndTime: null,
    setLessonEndTime: (endTime: string | null) => set({ lessonEndTime: endTime }),

    // Simplified video sync state
    videoSync: null,
    setVideoSync: (
      sync: {
        videoId: string;
        time: number;
        isPlaying: boolean;
        lastUpdate: number;
      } | null,
    ) => set({ videoSync: sync }),

    // Recording state
    isRecording: false,
    setIsRecording: (isRecording: boolean) => set({ isRecording }),
    recordingStartTime: null,
    setRecordingStartTime: (startTime: number | null) => set({ recordingStartTime: startTime }),
    recordingBlob: null,
    setRecordingBlob: (blob: Blob | null) => set({ recordingBlob: blob }),

    // Meeting error state
    meetingError: null,
    setMeetingError: (error: MeetingError | null) => set({ meetingError: error }),
    clearMeetingError: () => set({ meetingError: null }),
  })),
);
